'use client';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faAnglesLeft,
  faAnglesRight,
  faChevronLeft,
  faChevronRight,
  faDownload,
  faFilter,
  faMagnifyingGlass,
  faPlus,
  faRefresh
} from '@fortawesome/pro-regular-svg-icons';
import {
  Box,
  Button,
  Divider,
  IconButton,
  InputAdornment,
  MenuItem,
  outlinedInputClasses,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography
} from '@mui/material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import {
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable
} from '@tanstack/react-table';
import React, { useState, useEffect } from 'react';
import { Filter } from 'src/components/era-package/filters';
import ChildCard from 'src/components/era-package/shared/ChildCard';
import { useColumns } from './hooks';
import submissions from './submissions';
import { DetailModal } from '../DetailModal'

import pathMap from 'src/paths/pathMap';
import AbiEntrySummaryApiUrls from "../../../../apiUrls/AbiEntrySummaryApiUrls";
import { Link } from 'react-router-dom';

// Main Component
const ABISubmissions = () => {
  const [data, setData] = useState([]);
  const [globalFilter, setGlobalFilter] = useState('');
  const [columnFilters, setColumnFilters] = useState([]);
  const [showFilters, setShowFilters] = useState(false);
  const [selectedRow, setSelectedRow] = useState()
  const rerender = React.useReducer(() => ({}), {})[1];
  const [loading, setLoading] = useState(false);

  const [columnVisibility, setColumnVisibility] = useState({
    '1c': false, // true = visible, false = hidden
    'pga': false
  });

  const columns = useColumns();

  const fetchPage = () => {
    let isAlive = true
    setLoading(true)

    fetch(AbiEntrySummaryApiUrls.GET_ENTRY_LIST, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': 'Bearer ' + JSON.parse(localStorage.getItem('userToken'))
      },
    }).then(res => res.json())
      .then(async (json) => {
        if (isAlive && !!json.data) {
          setData(json.data);
          setLoading(false)
        }
      })
      .catch(err => {
        console.error(err);
        setLoading(false)
      })

    return () => {
      isAlive = false
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchPage()
  }, [])


  const handleRowClick = row => {
    setSelectedRow(row.original)
  }

  const table = useReactTable({
    data,
    columns,
    filterFns: {},
    state: {
      columnFilters,
      globalFilter,
      columnVisibility
    },
    onColumnVisibilityChange: setColumnVisibility,
    onColumnFiltersChange: setColumnFilters,
    onGlobalFilterChange: setGlobalFilter,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    globalFilterFn: 'includesString',
    debugTable: true,
    debugHeaders: true,
    debugColumns: false,
  });

  const handleDownload = () => {
    const headers = columns.map((col) => col.header());
    const rows = data.map((item) => [
      item.entryNumber,
      item.referenceNo,
      item.bl,
      item['3461Status'],
      item['7501Status'],
      item.pga,
      item['1c'],
      item['1usg'],
      item.entryPort,
      item.eta,
      item.statementDate,
      item.importer,
      item.consignee,
      item.user,
      item.lastModified,
    ]);

    const csvContent = [headers.join(','), ...rows.map((e) => e.join(','))].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);

    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', 'abi-submissions.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Add fallback for empty data
  if (!data || data.length === 0) {
    return (
      <Box>
        <Box sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary">
            No submissions data available
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Please check that the submissions data is properly imported and formatted.
          </Typography>
        </Box>
        <Box sx={{ p: 2, textAlign: 'center' }}>
          <Button
            component={Link}
            to={pathMap.ABI_ENTRY_SUMMARY_ADD_VIEW}
            variant="outlined"
            startIcon={<FontAwesomeIcon icon={faPlus} style={{ width: 16, height: 16 }} />}
          >
            Create Entry Summary
          </Button>
        </Box>
      </Box>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Stack spacing={2}>
        {/* Search and Filter Controls */}
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          alignItems="center"
          spacing={2}
          sx={{ mb: 2 }}
        >
          <TextField
            size="small"
            placeholder="Search all columns..."
            value={globalFilter ?? ''}
            onChange={(e) => setGlobalFilter(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <FontAwesomeIcon icon={faMagnifyingGlass} style={{ width: 16, height: 16 }} />
                </InputAdornment>
              ),
            }}
            sx={{
              maxWidth: { xs: '100%', sm: '300px' },
              [`& .${outlinedInputClasses.notchedOutline}`]: {
                inset: 0
              }
            }}
          />
          <Button
            variant="outlined"
            onClick={() => setShowFilters(!showFilters)}
            startIcon={<FontAwesomeIcon icon={faFilter} style={{ width: 16, height: 16 }} />}
          >
            {showFilters ? 'Hide Filters' : 'Show Column Filters'}
          </Button>
          {/* remove download btn
          <Button
            variant="outlined"
            onClick={handleDownload}
            startIcon={<FontAwesomeIcon icon={faDownload} style={{ width: 16, height: 16 }} />}
          >
            Download Data
          </Button> */}
          <Button
            component={Link}
            to={pathMap.ABI_ENTRY_SUMMARY_ADD_VIEW}
            variant="outlined"
            startIcon={<FontAwesomeIcon icon={faPlus} style={{ width: 16, height: 16 }} />}
          >
            Create Entry Summary
          </Button>
        </Stack>
        <ChildCard sx={{ p: 0, mb: -3 }}>
          <Stack>
            <TableContainer>
              <Table>
                <TableHead>
                  {table.getHeaderGroups().map((headerGroup) => (
                    <React.Fragment key={headerGroup.id}>
                      <TableRow>
                        {headerGroup.headers.map((header) => (
                          <TableCell key={header.id}>
                            <Typography
                              variant="body"
                              color={'primary.main'}
                              fontWeight="600"
                              {...{
                                className: header.column.getCanSort()
                                  ? 'cursor-pointer select-none'
                                  : '',
                                onClick: header.column.getToggleSortingHandler(),
                              }}
                            >
                              {header.isPlaceholder
                                ? null
                                : flexRender(
                                  header.column.columnDef.header,
                                  header.getContext(),
                                )}
                              {{
                                asc: ' 🔼',
                                desc: ' 🔽',
                              }[header.column.getIsSorted()] ?? null}
                            </Typography>
                          </TableCell>
                        ))}
                      </TableRow>
                      {showFilters && (
                        <TableRow>
                          {headerGroup.headers.map((header) => (
                            <TableCell key={header.id}>
                              {header.column.getCanFilter() && showFilters ? (
                                <div>
                                  <Filter column={header.column} />
                                </div>
                              ) : null}
                            </TableCell>
                          ))}
                        </TableRow>
                      )}
                    </React.Fragment>
                  ))}
                </TableHead>
                <TableBody>
                  {table.getRowModel().rows.map((row) => (<React.Fragment key={row.id}>
                    <TableRow sx={{
                      cursor: 'pointer',
                      transition: theme => theme.transitions.create(),
                      '&:hover': {
                        backgroundColor: 'action.hover'
                      }
                    }}
                      onClick={() => handleRowClick(row)}
                    >
                      {row.getVisibleCells().map((cell) => (
                        <TableCell key={cell.id} sx={cell.column.columnDef.meta?.style}>
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </TableCell>
                      ))}
                    </TableRow>
                  </React.Fragment>))}
                </TableBody>
              </Table>
            </TableContainer>
            <Divider />
            <Stack
              gap={1}
              p={3}
              alignItems="center"
              direction={{ xs: 'column', sm: 'row' }}
              justifyContent="space-between"
            >
              <Box display="flex" alignItems="center" gap={1}>
                <Button variant="contained" color="primary" onClick={() => fetchPage()}
                  startIcon={<FontAwesomeIcon icon={faRefresh} style={{ width: 16, height: 16 }} spin={loading} />}
                >
                  Refresh data
                </Button>
                <Typography variant="body1">
                  {table.getPrePaginationRowModel().rows.length} Rows
                </Typography>
              </Box>
              <Box
                sx={{
                  display: {
                    xs: 'block',
                    sm: 'flex',
                  },
                }}
                alignItems="center"
                gap={1}
              >
                <Stack direction="row" alignItems="center" gap={1}>
                  <Typography variant="body1">Page</Typography>
                  <Typography variant="body1" fontWeight={600}>
                    {table.getState().pagination.pageIndex + 1} of {table.getPageCount()}
                  </Typography>
                </Stack>
                <Stack direction="row" alignItems="center" gap={1}>
                  | Go to page:
                  <TextField
                    type="number"
                    min="1"
                    max={table.getPageCount()}
                    defaultValue={table.getState().pagination.pageIndex + 1}
                    onChange={(e) => {
                      const page = e.target.value ? Number(e.target.value) - 1 : 0;
                      table.setPageIndex(page);
                    }}
                    size="small"
                  />
                </Stack>
                <Select
                  value={table.getState().pagination.pageSize}
                  onChange={(e) => {
                    table.setPageSize(Number(e.target.value));
                  }}
                  size="small"
                >
                  {[5, 10, 15, 20, 25].map((pageSize) => (
                    <MenuItem key={pageSize} value={pageSize}>
                      {pageSize}
                    </MenuItem>
                  ))}
                </Select>

                <IconButton
                  size="small"
                  onClick={() => table.setPageIndex(0)}
                  disabled={!table.getCanPreviousPage()}
                >
                  <FontAwesomeIcon icon={faAnglesLeft} />
                </IconButton>
                <IconButton
                  size="small"
                  onClick={() => table.previousPage()}
                  disabled={!table.getCanPreviousPage()}
                >
                  <FontAwesomeIcon icon={faChevronLeft} />
                </IconButton>
                <IconButton
                  size="small"
                  onClick={() => table.nextPage()}
                  disabled={!table.getCanNextPage()}
                >
                  <FontAwesomeIcon icon={faChevronRight} />
                </IconButton>
                <IconButton
                  size="small"
                  onClick={() => table.setPageIndex(table.getPageCount() - 1)}
                  disabled={!table.getCanNextPage()}
                >
                  <FontAwesomeIcon icon={faAnglesRight} />
                </IconButton>
              </Box>
            </Stack>
          </Stack>
        </ChildCard>
      </Stack>

      <DetailModal id={selectedRow?.abiTransId}
        open={!!selectedRow}
        onClose={() => setSelectedRow(null)}
        fetchData={() => fetchPage()}
        rowData={selectedRow}
      />
    </LocalizationProvider>
  );
};

export default ABISubmissions;
